/**
 * Canonical recipient id ("contactId") for the notification system.
 *
 * The backend keys notifications, devices and push tokens on `user_master._id`
 * (see firebase_push_service / recipient_resolver). The JWT carries this as
 * `userMasterId`, so we read it from the access token — the redux `user` object
 * doesn't always include it and otherwise falls through to the personnel id.
 */

interface Jwtish {
  userMasterId?: string;
  personnelId?: string;
  [k: string]: unknown;
}

/** Decode the JWT payload without verifying (client-side display/keying only). */
function decodeAccessToken(): Jwtish | null {
  try {
    const token = localStorage.getItem('accessToken');
    if (!token) return null;
    const part = token.split('.')[1];
    if (!part) return null;
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json) as Jwtish;
  } catch {
    return null;
  }
}

/** user_master._id from the token, or '' if unavailable. */
export function getUserMasterId(): string {
  const payload = decodeAccessToken();
  return payload?.userMasterId ? String(payload.userMasterId) : '';
}

/** personnel_master._id from the token/local state, or '' if unavailable. */
export function getPersonnelId(user?: any): string {
  const payload = decodeAccessToken();
  return (
    (payload?.personnelId ? String(payload.personnelId) : '') ||
    user?.personnelId ||
    user?.personnelMasterId ||
    localStorage.getItem('personnelId') ||
    ''
  );
}

/**
 * Resolve the canonical contactId. Prefer the token's userMasterId (authoritative),
 * then any userMasterId on the passed user object, then fall back to _id/id.
 */
export function getContactId(user?: any): string {
  return (
    getUserMasterId() ||
    user?.userMasterId ||
    user?._id ||
    user?.id ||
    ''
  );
}

/**
 * Recipient id for notification documents/inbox queries.
 * The current emit path stores notifications by personnel_master._id.
 */
export function getNotificationContactId(user?: any): string {
  return getPersonnelId(user) || getContactId(user);
}
